import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  UserIcon,
  CalendarIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import DocumentReviewModal from './DocumentReviewModal';
import ManualDocumentUploadModal from './ManualDocumentUploadModal';
import { emailTemplateApi, EmailTemplate } from '../services/emailTemplateApi';

interface DocumentSubmission {
  id: number;
  fileName: string;
  fileSize: number;
  fileType: string;
  gcsFilePath: string;
  gcsBucket: string;
  uploadedAt: string;
  note?: string;
}

interface DocumentRequestItem {
  id: number;
  requestId: number;
  documentTypeId: number;
  status: 'pending' | 'submitted' | 'accepted' | 'rejected';
  submittedAt?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  documentType: {
    name: string;
    description?: string;
    isRequired: boolean;
    maxFileSizeMb: number;
    allowedFileTypes: string[];
  };
  submissions?: DocumentSubmission[];
}

interface DocumentRequest {
  id: number;
  organizationId: number;
  leadId: number;
  requestToken: string;
  status: 'requested' | 'submitted' | 'reviewed' | 'rejected';
  requestedBy: number;
  requestedAt: string;
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: number;
  reviewNotes?: string;
  isReRequest?: boolean;
  createdAt: string;
  updatedAt: string;
  lead: {
    firstName: string;
    lastName: string;
    email: string;
  };
  requestedByUser: {
    firstName: string;
    lastName: string;
  };
  reviewedByUser?: {
    firstName: string;
    lastName: string;
  };
  items: DocumentRequestItem[];
}

interface DocumentRequestDetailProps {
  request: DocumentRequest;
  onClose: () => void;
  onRequestUpdated: () => void;
}

const DocumentRequestDetail: React.FC<DocumentRequestDetailProps> = ({ 
  request, 
  onClose, 
  onRequestUpdated 
}) => {
  const [requestData, setRequestData] = useState<DocumentRequest>(request);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; fileName: string; fileType: string } | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showManualUploadModal, setShowManualUploadModal] = useState(false);

  useEffect(() => {
    fetchRequestDetails();
  }, [request.id]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await import('../services/api').then(module => 
        module.default.get(`/documents/requests/${request.id}`)
      );
      setRequestData(response.data.data);
    } catch (err: any) {
      console.error('Error fetching request details:', err);
      setError('Failed to fetch request details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'requested':
        return <ClockIcon className="w-4 h-4" />;
      case 'submitted':
        return <DocumentTextIcon className="w-4 h-4" />;
      case 'reviewed':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'rejected':
        return <XCircleIcon className="w-4 h-4" />;
      default:
        return <ExclamationTriangleIcon className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'requested':
        return 'bg-yellow-100 text-yellow-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'reviewed':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handlePreviewFile = async (submission: DocumentSubmission) => {
    try {
      // Get signed URL for the file
      const response = await import('../services/api').then(module => 
        module.default.get(`/documents/files/${submission.fileName}/download`)
      );

      if (response.data.success) {
        setPreviewFile({
          url: response.data.data.signedUrl,
          fileName: submission.fileName,
          fileType: submission.fileType
        });
      }
    } catch (error) {
      console.error('Error getting file preview:', error);
      setError('Error loading file preview');
    }
  };

  const handleDownloadFile = async (submission: DocumentSubmission) => {
    try {
      // Get signed URL for the file
      const response = await import('../services/api').then(module => 
        module.default.get(`/documents/files/${submission.fileName}/download`)
      );

      if (response.data.success) {
        // Create a temporary link to download the file
        const link = document.createElement('a');
        link.href = response.data.data.signedUrl;
        link.download = submission.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Error downloading file');
    }
  };

  const generateRequestLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/documents/submit/${requestData.requestToken}`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  // Fill template with data helper
  const fillTemplateWithData = (template: EmailTemplate) => {
    const filled = emailTemplateApi.fillTemplate(template, {
      leadName: `${requestData.lead.firstName} ${requestData.lead.lastName}`,
      leadEmail: requestData.lead.email,
      documentType: requestData.items?.map(item => item.documentType.name).join(', ') || 'documents',
      documentUploadLink: generateRequestLink()
    });
    
    setEmailSubject(filled.subject);
    setEmailMessage(filled.body);
  };

  // Load email templates when email modal opens
  useEffect(() => {
    const loadEmailTemplates = async () => {
      if (!showEmailModal) return;

      try {
        // Load document request email templates - use rejected documents templates for re-requests
        const response = requestData.isReRequest 
          ? await emailTemplateApi.getRejectedDocumentsTemplates()
          : await emailTemplateApi.getDocumentsTemplates();
        
        if (response.success && response.data && response.data.length > 0) {
          setAvailableTemplates(response.data);
          
          // Find the default template or use the first one
          const defaultTemplate = response.data.find(t => t.isDefault) || response.data[0];
          
          if (defaultTemplate) {
            setSelectedTemplate(defaultTemplate);
            fillTemplateWithData(defaultTemplate);
          }
        } else {
          // Fallback to default template if no templates are available
          setAvailableTemplates([]);
          setSelectedTemplate(null);
          
          const defaultSubject = requestData.isReRequest
            ? `Document Re-Request - ${requestData.items?.map(item => item.documentType.name).join(', ') || 'Documents'}`
            : `Document Request - ${requestData.items?.map(item => item.documentType.name).join(', ') || 'Documents'}`;
          const defaultMessage = requestData.isReRequest
            ? `Hello ${requestData.lead.firstName},\n\nWe need you to resubmit the following documents: ${requestData.items?.map(item => item.documentType.name).join(', ') || 'documents'}\n\nPlease click the link below to upload your documents:\n${generateRequestLink()}\n\nIf you have any questions, please don't hesitate to contact us.\n\nThank you for your cooperation.\n\nBest regards,\nYour Team`
            : `Hello ${requestData.lead.firstName},\n\nWe need you to submit the following documents: ${requestData.items?.map(item => item.documentType.name).join(', ') || 'documents'}\n\nPlease click the link below to upload your documents:\n${generateRequestLink()}\n\nIf you have any questions, please don't hesitate to contact us.\n\nThank you for your cooperation.\n\nBest regards,\nYour Team`;
          
          setEmailSubject(defaultSubject);
          setEmailMessage(defaultMessage);
        }
      } catch (error) {
        console.error('Error loading email template:', error);
        setAvailableTemplates([]);
        setSelectedTemplate(null);
        
        // Fallback to default template on error
        const defaultSubject = requestData.isReRequest
          ? `Document Re-Request - ${requestData.items?.map(item => item.documentType.name).join(', ') || 'Documents'}`
          : `Document Request - ${requestData.items?.map(item => item.documentType.name).join(', ') || 'Documents'}`;
        const defaultMessage = requestData.isReRequest
          ? `Hello ${requestData.lead.firstName},\n\nWe need you to resubmit the following documents: ${requestData.items?.map(item => item.documentType.name).join(', ') || 'documents'}\n\nPlease click the link below to upload your documents:\n${generateRequestLink()}\n\nIf you have any questions, please don't hesitate to contact us.\n\nThank you for your cooperation.\n\nBest regards,\nYour Team`
          : `Hello ${requestData.lead.firstName},\n\nWe need you to submit the following documents: ${requestData.items?.map(item => item.documentType.name).join(', ') || 'documents'}\n\nPlease click the link below to upload your documents:\n${generateRequestLink()}\n\nIf you have any questions, please don't hesitate to contact us.\n\nThank you for your cooperation.\n\nBest regards,\nYour Team`;
        
        setEmailSubject(defaultSubject);
        setEmailMessage(defaultMessage);
      }
    };

    loadEmailTemplates();
  }, [showEmailModal, requestData.items, requestData.lead.firstName, requestData.lead.lastName, requestData.lead.email, requestData.isReRequest]);

  // Handle template selection change
  const handleTemplateChange = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    fillTemplateWithData(template);
  };

  const sendEmailRequest = async () => {
    // Open email modal instead of sending directly
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    try {
      setSendingEmail(true);
      setError(null);
      
      const response = await import('../services/api').then(module => 
        module.default.post('/documents/requests/send-email', {
          requestToken: requestData.requestToken,
          recipientEmail: requestData.lead.email,
          recipientName: `${requestData.lead.firstName} ${requestData.lead.lastName}`,
          subject: emailSubject,
          message: emailMessage
        })
      );
      
      if (response.data.success) {
        setShowEmailModal(false);
        // Show success message
        alert('Email sent successfully!');
      }
    } catch (err: any) {
      console.error('Error sending email:', err);
      setError('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Document Request Details</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Request Overview */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900">
                    {requestData.lead.firstName} {requestData.lead.lastName}
                  </h4>
                  <p className="text-sm text-gray-600">{requestData.lead.email}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(requestData.status)}`}>
                  {getStatusIcon(requestData.status)}
                  <span className="ml-2 capitalize">{requestData.status}</span>
                </span>
                {requestData.isReRequest && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                    Re-Request
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Requested by:</span>
                <p className="text-gray-600">
                  {requestData.requestedByUser.firstName} {requestData.requestedByUser.lastName}
                </p>
              </div>
              <div>
                <span className="font-medium text-gray-700">Requested on:</span>
                <p className="text-gray-600">{formatDate(requestData.requestedAt)}</p>
              </div>
              {requestData.submittedAt && (
                <div>
                  <span className="font-medium text-gray-700">Submitted on:</span>
                  <p className="text-gray-600">{formatDate(requestData.submittedAt)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Request Link */}
          {requestData.status === 'requested' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="text-sm font-medium text-blue-900 mb-2">Request Link</h5>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={generateRequestLink()}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded-md bg-white"
                />
                <button
                  onClick={() => copyToClipboard(generateRequestLink())}
                  className="px-3 py-2 text-sm text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md"
                >
                  Copy Link
                </button>
                <button
                  onClick={sendEmailRequest}
                  disabled={loading}
                  className="px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  Send Email
                </button>
              </div>
            </div>
          )}

          {/* Manual Upload Button */}
          {(requestData.status === 'requested' || requestData.status === 'submitted') && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-medium text-blue-900 mb-1">Upload on Behalf of Client</h5>
                  <p className="text-xs text-blue-700">
                    Upload documents manually for {requestData.lead.firstName} {requestData.lead.lastName}
                  </p>
                </div>
                <button
                  onClick={() => setShowManualUploadModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <CloudArrowUpIcon className="w-4 h-4 mr-2" />
                  Upload Documents
                </button>
              </div>
            </div>
          )}

          {/* Document Types */}
          <div>
            <h5 className="text-sm font-medium text-gray-900 mb-3">Requested Documents</h5>
            <div className="space-y-3">
              {requestData.items?.map((item) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                      <div>
                        <h6 className="text-sm font-medium text-gray-900">
                          {item.documentType.name}
                        </h6>
                        {item.documentType.description && (
                          <p className="text-xs text-gray-600">{item.documentType.description}</p>
                        )}
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {getStatusIcon(item.status)}
                      <span className="ml-1 capitalize">{item.status}</span>
                    </span>
                  </div>

                  <div className="text-xs text-gray-500 space-y-1">
                    <div>Max file size: {item.documentType.maxFileSizeMb}MB</div>
                    <div>Allowed types: {item.documentType.allowedFileTypes.join(', ')}</div>
                    {item.documentType.isRequired && (
                      <div className="text-red-600 font-medium">Required document</div>
                    )}
                  </div>

                  {item.status === 'submitted' && item.submittedAt && (
                    <div className="mt-2 text-xs text-gray-600">
                      Submitted on: {formatDate(item.submittedAt)}
                    </div>
                  )}

                  {item.rejectionReason && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                      <strong>Rejection reason:</strong> {item.rejectionReason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Review Section */}
          {requestData.status === 'submitted' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h5 className="text-sm font-medium text-yellow-900 mb-2">Ready for Review</h5>
              <p className="text-sm text-yellow-700 mb-3">
                This request has been submitted and is ready for your review.
              </p>
              <button
                onClick={() => setShowReviewModal(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700"
              >
                <EyeIcon className="w-4 h-4 mr-2" />
                Review Documents
              </button>
            </div>
          )}

          {/* Review Notes */}
          {requestData.reviewNotes && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h5 className="text-sm font-medium text-gray-900 mb-2">Review Notes</h5>
              <p className="text-sm text-gray-700">{requestData.reviewNotes}</p>
              {requestData.reviewedByUser && (
                <p className="text-xs text-gray-500 mt-2">
                  Reviewed by {requestData.reviewedByUser.firstName} {requestData.reviewedByUser.lastName} on {formatDate(requestData.reviewedAt!)}
                </p>
              )}
            </div>
          )}

          {/* Uploaded Documents - Show after review */}
          {requestData.status === 'reviewed' && requestData.items && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h5 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <DocumentTextIcon className="w-5 h-5 mr-2 text-gray-400" />
                Uploaded Documents
              </h5>
              <div className="space-y-4">
                {requestData.items
                  .filter(item => item.status === 'accepted' && item.submissions && item.submissions.length > 0)
                  .map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                          <div>
                            <h6 className="text-sm font-medium text-gray-900">
                              {item.documentType.name}
                            </h6>
                            <p className="text-xs text-gray-500">
                              Status: <span className="text-green-600 font-medium">Accepted</span>
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {item.submissions?.map((submission) => (
                          <div key={submission.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                            <div className="flex items-center space-x-3">
                              <DocumentTextIcon className="w-4 h-4 text-gray-400" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{submission.fileName}</p>
                                <p className="text-xs text-gray-500">
                                  {formatFileSize(submission.fileSize)} • {formatDate(submission.uploadedAt)}
                                </p>
                                {submission.note && (
                                  <p className="text-xs text-gray-600 mt-1 italic">"{submission.note}"</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {(submission.fileType.startsWith('image/') || submission.fileType === 'application/pdf') && (
                                <button
                                  onClick={() => handlePreviewFile(submission)}
                                  className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                                >
                                  <EyeIcon className="w-3 h-3 mr-1" />
                                  Preview
                                </button>
                              )}
                              <button
                                onClick={() => handleDownloadFile(submission)}
                                className="inline-flex items-center px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                              >
                                <ArrowDownTrayIcon className="w-3 h-3 mr-1" />
                                Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                
                {requestData.items.filter(item => item.status === 'accepted' && item.submissions && item.submissions.length > 0).length === 0 && (
                  <div className="text-center py-8">
                    <DocumentTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No accepted documents to display</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Review Modal */}
        {showReviewModal && requestData.items && (
          <DocumentReviewModal
            request={requestData}
            onClose={() => setShowReviewModal(false)}
            onRequestUpdated={onRequestUpdated}
          />
        )}

        {/* File Preview Modal */}
        {previewFile && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {previewFile.fileName}
                </h3>
                <button
                  onClick={() => setPreviewFile(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
              <div className="p-4">
                {previewFile.fileType.startsWith('image/') ? (
                  <img
                    src={previewFile.url}
                    alt={previewFile.fileName}
                    className="max-w-full max-h-[70vh] mx-auto"
                  />
                ) : previewFile.fileType === 'application/pdf' ? (
                  <iframe
                    src={previewFile.url}
                    className="w-full h-[70vh] border-0"
                    title={previewFile.fileName}
                  />
                ) : (
                  <div className="text-center py-8">
                    <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">Preview not available for this file type</p>
                    <button
                      onClick={() => {
                        const link = document.createElement('a');
                        link.href = previewFile.url;
                        link.download = previewFile.fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                      Download File
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Manual Upload Modal */}
        {showManualUploadModal && (
          <ManualDocumentUploadModal
            request={requestData}
            onClose={() => setShowManualUploadModal(false)}
            onUploadSuccess={async () => {
              await fetchRequestDetails();
              onRequestUpdated();
            }}
          />
        )}

        {/* Email Modal */}
        {showEmailModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowEmailModal(false)}>
            <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Send Email</h3>
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To:
                  </label>
                  <input
                    type="email"
                    value={requestData.lead.email}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                {/* Template Selector */}
                {availableTemplates.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Template:
                    </label>
                    <select
                      value={selectedTemplate?.id || ''}
                      onChange={(e) => {
                        const template = availableTemplates.find(t => t.id === parseInt(e.target.value));
                        if (template) handleTemplateChange(template);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      {availableTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.title} {template.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject:
                  </label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message:
                  </label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowEmailModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !emailSubject || !emailMessage}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentRequestDetail;
